import { Component } from 'react';
import { connect } from 'react-redux';
import { isEqual } from 'underscore';
import { userInfoSync } from 'hub-http/userInfo';
import Routes from 'ContentUtils/Routes';
import FormattedMessage from 'I18n/components/FormattedMessage';
import AsyncSelector from 'ContentComponents/components/cmv2/components/AsyncSelector';
import { Button } from 'foundations-components/transitional/components/Button';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import * as MeetingsActions from 'ContentData/actions/Meetings';
import * as UserActions from 'ContentData/actions/Users';
import { getDefaultMeetingsOptions, getMeeting, getMeetingsOptions, getMeetingsCacheRequestStatus } from 'ContentData/selectors/meetings';
import { getUsers } from 'ContentData/selectors/users';
import { DEFAULT_PAGE_SIZE } from 'ContentComponents/components/cmv2/constants';
import { MEETING } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import { ExternalLinkIcon } from 'foundations-assets';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const mapStateToProps = (state, props) => ({
  defaultOptions: getDefaultMeetingsOptions(state),
  meeting: getMeeting(state, {
    link: props.value
  }),
  options: getMeetingsOptions(state),
  optionsRequestStatus: getMeetingsCacheRequestStatus(state),
  users: getUsers(state)
});
const mapDispatchToProps = {
  clearCache: MeetingsActions.clearMeetings,
  fetchMeeting: MeetingsActions.fetchMeeting,
  fetchMeetings: MeetingsActions.fetchMeetings,
  fetchUsers: UserActions.fetchUsersForTeams,
  saveCache: MeetingsActions.saveCache
};
const connector = connect(mapStateToProps, mapDispatchToProps);
const i18nKey = path => `ContentComponents.${path}`;
class MeetingSelect extends Component {
  constructor(props) {
    super(props);
    const {
      user
    } = userInfoSync();
    this.hasPersonalCreateAccess = user.scopes.includes('meetings-personal-link-create');
    this.hasTeamCreateAccess = user.scopes.includes('meetings-team-link-create');
    this.userId = user.user_id;
    this.hasUniversalEditAccess = user.scopes.includes('meetings-edit-all-links');
    this.onChange = this.onChange.bind(this);
    this.handleLoadOptions = this.handleLoadOptions.bind(this);
    this.handleCreateMeeting = this.handleCreateMeeting.bind(this);
    this.handleEditMeeting = this.handleEditMeeting.bind(this);
    this.state = {
      hasMore: true,
      input: ''
    };
  }
  componentDidMount() {
    const {
      fetchMeeting,
      fetchUsers,
      value
    } = this.props;
    if (value && typeof value === 'string') {
      fetchMeeting(value).catch(err => {
        throw err;
      });
    }
    fetchUsers().catch(err => {
      throw err;
    });
  }
  componentDidUpdate(prevProps) {
    const {
      fetchMeeting,
      meeting,
      value
    } = this.props;
    const {
      value: prevValue
    } = prevProps;
    if (value && !isEqual(prevValue, value) && !meeting) {
      fetchMeeting(value).catch(err => {
        throw err;
      });
    }
  }
  onChange({
    target: {
      value
    }
  }) {
    const {
      fetchMeeting,
      onFieldChange
    } = this.props;
    fetchMeeting(value).catch(err => {
      throw err;
    });
    onFieldChange(value);
  }
  handleLoadOptions(input, callback, pagination = {
    after: ''
  }) {
    let after = pagination.after;
    if (input !== this.state.input) {
      this.props.clearCache();
      this.setState({
        hasMore: true,
        input
      });
      after = '';
    }
    const query = {
      after,
      limit: DEFAULT_PAGE_SIZE,
      search: input
    };
    this.props.fetchMeetings(query).then(response => {
      const {
        paging
      } = response;
      const hasMore = paging && paging.next;

      // Has to be a conditional because we only want `hasMore` to reset
      // if the entire meetings cache is cleared.
      if (!hasMore) this.setState({
        hasMore: false
      });
      callback(null, {
        options: this.props.options || [],
        pagination: {
          after: this.state.hasMore && paging.next.after,
          hasMore: this.state.hasMore
        }
      });
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  }
  handleCreateMeeting() {
    window.open(Routes.createMeeting());
  }
  handleEditMeeting() {
    const {
      meeting
    } = this.props;
    const {
      id: meetingId,
      portalId
    } = meeting;
    window.open(Routes.editMeeting(portalId, meetingId));
  }
  renderCreateMeetingButton() {
    const {
      hasTeamCreateAccess,
      hasPersonalCreateAccess
    } = this;
    const {
      readOnly
    } = this.props;
    if (hasPersonalCreateAccess || hasTeamCreateAccess) {
      return /*#__PURE__*/_jsx(Button, {
        size: "xs",
        disabled: readOnly,
        "data-test-id": "create-meeting-button",
        onClick: this.handleCreateMeeting,
        marginInlineStart: "200",
        use: "tertiary-light",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ContentComponents.cmv2.fields.baseResource.add"
        })
      });
    }
    return null;
  }
  render() {
    const {
      clearCache,
      defaultOptions,
      options,
      optionsRequestStatus,
      saveCache,
      useOnDark,
      meeting,
      readOnly,
      required,
      value
    } = this.props;
    const isEditButtonEnabled = value && meeting && (this.hasUniversalEditAccess || meeting.userId === this.userId);
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(AsyncSelector, {
        clearable: !required,
        clearCache: clearCache,
        defaultOptions: defaultOptions,
        loadOptions: this.handleLoadOptions,
        options: options,
        optionsRequestStatus: optionsRequestStatus,
        onChange: this.onChange,
        readOnly: readOnly,
        saveCache: saveCache,
        type: MEETING,
        useOnDark: useOnDark,
        value: value
      }), /*#__PURE__*/_jsxs(TFlex, {
        marginBlockStart: "200",
        children: [/*#__PURE__*/_jsx(Button, {
          use: "tertiary-light",
          "data-test-id": "edit-meeting-button",
          size: "xs",
          disabled: !isEditButtonEnabled,
          onClick: this.handleEditMeeting,
          marginInlineStart: "0",
          children: /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            gap: "100",
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('buttons.edit')
            }), /*#__PURE__*/_jsx(ExternalLinkIcon, {
              size: "100"
            })]
          })
        }), this.renderCreateMeetingButton()]
      })]
    });
  }
}
export default connector(MeetingSelect);